/// <amd-module name="Core/Medius.Core.Web/Scripts/components/emailStatusTab/components/emailStatusList"/>

import { ContentAlignEnum, EmptyState, EmptyStateSizeEnum, FlexLayout, ImageMessagessInbox, Text, LoaderIcon, Button, IconArrowsClockwiseRegular } from '@medius/ui-controls';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { EmailEntryDto } from '../models/emailEntryDto';
import { EmailEntry } from './emailEntry';
import { getEmailStatuses } from '../api/emailTrackerApi';
import { handleError } from 'Core/Medius.Core.Web/Scripts/lib/errorHandling/errorHandler';
import { translate } from 'Core/Medius.Core.Web/Scripts/lib/globalization';
import { getCompanyUsers } from 'Core/Medius.Core.Web/Scripts/components/sendEmailCapture/sendEmailContainerService';
import { Contact } from 'Core/Medius.Core.Web/Scripts/components/sendEmailDialog/types';

type EmailStatusListProps = {
    documentId: string;
    companyId: number;
    importTrackingId?: string;
    documentType?: string;
    hashFiles?: { Type: string, Hash: string }[];
    userTaskId?: number;
};

export const EmailStatusList = ({ documentId, importTrackingId, documentType, hashFiles, userTaskId, companyId }: EmailStatusListProps): React.ReactElement => {
    const [emailEntries, setEmailEntries] = useState<EmailEntryDto[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [companyUsers, setCompanyUsers] = useState<Contact[]>([]);

    React.useEffect(() => {
        getCompanyUsers(companyId).then((result => setCompanyUsers(result.map(u => ({ displayName: u.text, emailAddress: u.value })))));
    }, [companyId]);


    const fetchEmailStatuses = useCallback(async () => {
        if (!documentId && !importTrackingId) return;
        setIsLoading(true);
        try {
            const result = await getEmailStatuses(documentId, importTrackingId);
            setEmailEntries(result);
        } catch (e) {
            handleError(e);
        } finally {
            setIsLoading(false);
        }
    }, [documentId, importTrackingId, hashFiles, documentType, userTaskId]);

    useEffect(() => {
        fetchEmailStatuses();
    }, [documentId, importTrackingId, documentType, userTaskId, fetchEmailStatuses]);

    const handleRefresh = () => {
        fetchEmailStatuses();
    };

    return (
        <>
            {(isLoading || emailEntries?.length !== 0) &&
                <FlexLayout direction="row" style={{ padding: '10px 10px 0px 10px', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text variant="heading3" style={{ fontWeight: 'bold', fontSize: '16px' }}>{translate('#Core/emailStatus')}</Text>
                    <Button
                        icon={<IconArrowsClockwiseRegular />}
                        label={translate('#Core/refresh')}
                        variant='secondary'
                        height='small'
                        onClick={handleRefresh}
                    />
                </FlexLayout>}

            {isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                    <LoaderIcon delayDisplay={false} />
                </div>
            ) : emailEntries.length === 0 ? (
                <div className="email-status-empty">
                    <EmptyState
                        data-testid="email-status-empty"
                        size={EmptyStateSizeEnum.Large}
                        title={translate('#Core/noEmailsSentYet')}
                        explanation={translate('#Core/noEmailsSentYetExplanation')}
                        centerInContainer
                        renderActions={
                            () => <Button
                                icon={<IconArrowsClockwiseRegular />}
                                label={translate('#Core/refresh')}
                                variant='secondary'
                                height='small'
                                onClick={handleRefresh}
                            />
                        }
                        contentAlign={ContentAlignEnum.Center}
                        image={<ImageMessagessInbox style={{ maxWidth: 120, margin: '0 auto' }} />}
                    />
                </div>
            ) : (
                <FlexLayout direction="column" itemRowSpacing="none" className="email-status-list">
                    {emailEntries.map((emailEntry) => (
                        <EmailEntry
                            key={emailEntry.id}
                            emailEntry={emailEntry} 
                            emailMetadata={{
                                docId: Number(documentId),
                                docType: documentType,
                                hash: hashFiles?.[0]?.Hash,
                                tag: hashFiles?.[0]?.Type,
                                ccfInvoiceId: importTrackingId, 
                                userTaskId: userTaskId
                            }}
                            addressBook={companyUsers}
                        />
                    ))}
                </FlexLayout>
            )}
        </>
    );
};